import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Table,
  Button,
  Drawer,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Tag,
  Modal,
  message,
  DatePicker,
  Pagination,
} from 'antd';
import { PlusOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useAuth } from '../../contexts/AuthContext';
import { PERMISSIONS } from '../../config/permissions';
import DashboardPageWrapper from '../../components/layout/DashboardPageWrapper';
import specialPricingRequestsService from '../../services/specialPricingRequestsService';
import productService from '../../services/productService';
import ifaService from '../../services/ifaService';
import useRemoteSelectPage from '../../hooks/useRemoteSelectPage';

const SELECT_PAGE_SIZE = 50;

const getProductId = (product) => product?.id;
const getPartnerKey = (partner) => partner?.uuid ?? partner?.id;

function buildPartnerOption(partner) {
  const value = getPartnerKey(partner);
  return {
    value,
    label: [partner?.name, partner?.email, partner?.pan].filter(Boolean).join(' · ') || String(value),
  };
}

function buildProductOption(product) {
  const value = getProductId(product);
  return {
    value,
    label: product?.name || product?.short_name || String(value),
  };
}

const STATUS_COLORS = {
  PENDING: 'gold',
  APPROVED: 'green',
  REJECTED: 'red',
  CANCELLED: 'default',
  EXPIRED: 'default',
};

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'EXPIRED', label: 'Expired' },
];

export default function SpecialPricingRequestsPage() {
  const { hasPermission, isSuperAdmin } = useAuth();
  const canCreate = isSuperAdmin || hasPermission(PERMISSIONS.PRICING_SPECIAL_PRICING_REQUESTS_CREATE);
  const canApprove = isSuperAdmin || hasPermission(PERMISSIONS.PRICING_SPECIAL_PRICING_REQUESTS_APPROVE);
  const validityFieldReadOnly = canCreate && !canApprove;

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [form] = Form.useForm();
  const [rejectForm] = Form.useForm();

  const fetchProductsPage = useCallback(async ({ page: p, limit, search, signal }) => {
    const response = await productService.getProducts({
      page: p,
      limit,
      search,
      sort_by: 'preference_index',
      sort_order: 'asc',
      signal,
    });
    const data = response?.data ?? response;
    const list = data?.products ?? data?.data ?? [];
    return { items: Array.isArray(list) ? list : [], pagination: data?.pagination };
  }, []);

  const fetchPartnersPage = useCallback(async ({ page: p, limit, search, signal }) => {
    const result = await ifaService.getAdminIfas({
      page: p,
      limit,
      search,
      status: 'all',
      signal,
    });
    const list = result?.users ?? result?.data ?? result?.items ?? [];
    return { items: Array.isArray(list) ? list : [], pagination: result?.pagination };
  }, []);

  const {
    items: products,
    loading: productsLoading,
    handleSearch: handleProductSearch,
    handlePopupScroll: handleProductPopupScroll,
    handleOpenChange: handleProductOpenChange,
    dropdownRender: productDropdownRender,
    searchValue: productSearchValue,
  } = useRemoteSelectPage({
    fetchPage: fetchProductsPage,
    getKey: getProductId,
    pageSize: SELECT_PAGE_SIZE,
    onError: (e) =>
      message.error(e?.response?.data?.message || e?.message || 'Failed to load products'),
  });

  const {
    items: partners,
    loading: partnersLoading,
    handleSearch: handlePartnerSearch,
    handlePopupScroll: handlePartnerPopupScroll,
    handleOpenChange: handlePartnerOpenChange,
    dropdownRender: partnerDropdownRender,
    searchValue: partnerSearchValue,
  } = useRemoteSelectPage({
    fetchPage: fetchPartnersPage,
    getKey: getPartnerKey,
    pageSize: SELECT_PAGE_SIZE,
    onError: (e) =>
      message.error(e?.response?.data?.message || e?.message || 'Failed to load partners'),
  });

  const productOptions = useMemo(
    () =>
      products
        .map(buildProductOption)
        .filter((option) => option.value != null && option.value !== ''),
    [products],
  );

  const partnerOptions = useMemo(
    () =>
      partners
        .map(buildPartnerOption)
        .filter((option) => option.value != null && option.value !== ''),
    [partners],
  );

  const loadRequests = useCallback(async () => {
    setLoading(true);
    try {
      const res = await specialPricingRequestsService.list({
        page,
        limit: pageSize,
        status: statusFilter || undefined,
      });
      const data = res?.data ?? res;
      setRequests(data?.requests ?? []);
      setTotal(data?.pagination?.total ?? 0);
    } catch (e) {
      message.error(e?.response?.data?.message || 'Failed to load requests.');
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, statusFilter]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const openCreate = () => {
    form.resetFields();
    form.setFieldsValue({
      valid_until: dayjs().add(24, 'hour'),
    });
    setDrawerOpen(true);
  };

  const onCreate = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      await specialPricingRequestsService.create({
        partner_id: values.partner_id,
        product_id: values.product_id,
        requested_qty: values.requested_qty,
        requested_unit_price: values.requested_unit_price,
        ...(validityFieldReadOnly ? {} : { valid_until: values.valid_until?.toISOString() }),
        justification: values.justification,
      });
      message.success('Special pricing request submitted.');
      setDrawerOpen(false);
      loadRequests();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || 'Failed to create request.');
    } finally {
      setSubmitting(false);
    }
  };

  const onApprove = async (record) => {
    setActionLoading(true);
    try {
      await specialPricingRequestsService.approve(record.uuid);
      message.success(`${record.request_code} approved.`);
      loadRequests();
    } catch (e) {
      message.error(e?.response?.data?.message || 'Approval failed.');
    } finally {
      setActionLoading(false);
    }
  };

  const openReject = (record) => {
    setSelectedRequest(record);
    rejectForm.setFieldsValue({ reason: '' });
    setRejectModalOpen(true);
  };

  const onReject = async () => {
    try {
      const values = await rejectForm.validateFields();
      setActionLoading(true);
      await specialPricingRequestsService.reject(selectedRequest.uuid, {
        reason: values.reason,
      });
      message.success(`${selectedRequest.request_code} rejected.`);
      setRejectModalOpen(false);
      loadRequests();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || 'Reject failed.');
    } finally {
      setActionLoading(false);
    }
  };

  const onCancel = async (record) => {
    setActionLoading(true);
    try {
      await specialPricingRequestsService.cancel(record.uuid);
      message.success(`${record.request_code} cancelled.`);
      loadRequests();
    } catch (e) {
      message.error(e?.response?.data?.message || 'Cancel failed.');
    } finally {
      setActionLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      { title: 'Ref', dataIndex: 'request_code', key: 'request_code', width: 120 },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 110,
        render: (v) => <Tag color={STATUS_COLORS[v] || 'default'}>{v}</Tag>,
      },
      { title: 'Partner', dataIndex: 'partner_name', key: 'partner_name' },
      { title: 'Product', dataIndex: 'product_name', key: 'product_name' },
      { title: 'Qty', dataIndex: 'requested_qty', key: 'requested_qty', width: 80 },
      {
        title: 'Price',
        dataIndex: 'requested_unit_price',
        key: 'requested_unit_price',
        width: 110,
        render: (v) => (v != null ? `₹${Number(v).toLocaleString('en-IN')}` : ''),
      },
      {
        title: 'Group price',
        key: 'group_price',
        width: 110,
        render: (_, r) => {
          const gp = r.context_snapshot?.current_group_unit_price;
          return gp != null ? `₹${Number(gp).toLocaleString('en-IN')}` : '—';
        },
      },
      { title: 'RM', dataIndex: 'requested_by_name', key: 'requested_by_name', width: 140 },
      {
        title: 'Valid until',
        dataIndex: 'valid_until',
        key: 'valid_until',
        width: 170,
        render: (v) => (v ? dayjs(v).format('DD MMM YYYY, HH:mm') : '—'),
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 220,
        render: (_, record) => (
          <Space size="small">
            {canApprove && record.status === 'PENDING' && (
              <>
                <Button
                  type="primary"
                  size="small"
                  icon={<CheckOutlined />}
                  loading={actionLoading}
                  onClick={() => onApprove(record)}
                >
                  Approve
                </Button>
                <Button
                  size="small"
                  danger
                  icon={<CloseOutlined />}
                  onClick={() => openReject(record)}
                >
                  Reject
                </Button>
              </>
            )}
            {canCreate && record.status === 'PENDING' && !canApprove && (
              <Button size="small" onClick={() => onCancel(record)} loading={actionLoading}>
                Cancel
              </Button>
            )}
          </Space>
        ),
      },
    ],
    [canApprove, canCreate, actionLoading],
  );

  return (
    <DashboardPageWrapper>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Special Price Requests</h1>
          <p className="text-sm text-gray-500 mt-1 max-w-3xl">
            Sales RMs propose partner pricing; Central Team approves to create special tiers.
          </p>
        </div>
        {canCreate && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            New request
          </Button>
        )}
      </div>

      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          style={{ width: 180 }}
          value={statusFilter}
          options={STATUS_OPTIONS}
          onChange={(v) => {
            setStatusFilter(v);
            setPage(1);
          }}
        />
        <Button onClick={loadRequests}>Refresh</Button>
      </Space>

      <Table
        rowKey="uuid"
        columns={columns}
        dataSource={requests}
        loading={loading}
        pagination={false}
        scroll={{ x: 1200 }}
      />

      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
        <Pagination
          current={page}
          pageSize={pageSize}
          total={total}
          showSizeChanger
          onChange={(p, ps) => {
            setPage(p);
            setPageSize(ps);
          }}
        />
      </div>

      <Drawer
        title="New special price request"
        width={480}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        destroyOnClose
        footer={
          <Space>
            <Button onClick={() => setDrawerOpen(false)}>Cancel</Button>
            <Button type="primary" loading={submitting} onClick={onCreate}>
              Submit
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="partner_id"
            label="Partner / IFA"
            rules={[{ required: true, message: 'Select a partner.' }]}
          >
            <Select
              showSearch
              autoClearSearchValue={false}
              placeholder="Search partner by name, email, or PAN"
              filterOption={false}
              optionFilterProp="label"
              options={partnerOptions}
              loading={partnersLoading}
              notFoundContent={partnersLoading ? 'Loading…' : 'No partners found.'}
              searchValue={partnerSearchValue}
              onSearch={handlePartnerSearch}
              onPopupScroll={handlePartnerPopupScroll}
              onOpenChange={handlePartnerOpenChange}
              dropdownRender={partnerDropdownRender}
            />
          </Form.Item>
          <Form.Item
            name="product_id"
            label="Product share"
            rules={[{ required: true, message: 'Select a product.' }]}
          >
            <Select
              showSearch
              autoClearSearchValue={false}
              placeholder="Search product"
              filterOption={false}
              optionFilterProp="label"
              options={productOptions}
              loading={productsLoading}
              notFoundContent={productsLoading ? 'Loading…' : 'No products found.'}
              searchValue={productSearchValue}
              onSearch={handleProductSearch}
              onPopupScroll={handleProductPopupScroll}
              onOpenChange={handleProductOpenChange}
              dropdownRender={productDropdownRender}
            />
          </Form.Item>
          <Form.Item
            name="requested_qty"
            label="Quantity"
            rules={[{ required: true, message: 'Enter quantity.' }]}
          >
            <InputNumber min={1} precision={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="requested_unit_price"
            label="Unit price (₹)"
            rules={[{ required: true, message: 'Enter unit price.' }]}
          >
            <InputNumber min={0.01} precision={2} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="valid_until"
            label="Valid until"
            rules={[
              { required: true },
              {
                validator: (_, value) => {
                  if (validityFieldReadOnly || !value) return Promise.resolve();
                  if (dayjs(value).isBefore(dayjs())) {
                    return Promise.reject(new Error('Valid until must be in the future.'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
            extra={validityFieldReadOnly ? 'Fixed at 24 hours from submission for Sales requests.' : undefined}
          >
            <DatePicker
              showTime
              style={{ width: '100%' }}
              disabled={validityFieldReadOnly}
              disabledDate={(current) => current && current < dayjs().startOf('day')}
            />
          </Form.Item>
          <Form.Item name="justification" label="Justification (optional)">
            <Input.TextArea rows={3} maxLength={2000} />
          </Form.Item>
        </Form>
      </Drawer>

      <Modal
        title={`Reject ${selectedRequest?.request_code || ''}`}
        open={rejectModalOpen}
        onCancel={() => setRejectModalOpen(false)}
        onOk={onReject}
        confirmLoading={actionLoading}
        okText="Reject"
        okButtonProps={{ danger: true }}
      >
        <Form form={rejectForm} layout="vertical">
          <Form.Item
            name="reason"
            label="Reason"
            rules={[{ required: true, message: 'Enter a rejection reason.' }]}
          >
            <Input.TextArea rows={3} maxLength={2000} />
          </Form.Item>
        </Form>
      </Modal>
    </DashboardPageWrapper>
  );
}
